/**
 * SalePriceSheet — Premium bottom sheet for entering sale prices.
 *
 * Supports single or batch marking-as-sold.
 * TTCP chip: tappable pill that auto-fills the price input (never pre-filled).
 * All inputs must have a value > 0 before the submit button enables.
 *
 * Critical: TTCP is NEVER pre-filled. Field always starts blank.
 * This preserves forensic delta data (TTCP vs actual sale price).
 *
 * z-index 210: above BottomTabs (z-sticky = 200).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { T } from '@/styles/tokens';
import type { VerdictSummary } from '@/types';

function haptic(style: 'light' | 'medium') {
  if (navigator.vibrate) {
    navigator.vibrate(style === 'light' ? [8] : [15]);
  }
}

function displayPrice(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

interface SalePriceSheetProps {
  artworks: VerdictSummary[];
  onSubmit: (prices: Record<string, number>) => void;
  onClose: () => void;
}

export function SalePriceSheet({ artworks, onSubmit, onClose }: SalePriceSheetProps) {
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [tappedChips, setTappedChips] = useState<Set<string>>(new Set());
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const isSingle = artworks.length === 1;

  // Auto-focus first input on mount
  useEffect(() => {
    const first = artworks[0];
    if (first) {
      setTimeout(() => inputRefs.current[first.id]?.focus(), 300);
    }
  }, [artworks]);

  const allFilled = artworks.every(a => {
    const val = parseFloat(prices[a.id] || '');
    return !isNaN(val) && val > 0;
  });

  const fillTTCP = useCallback((id: string, ttcpCents: number) => {
    const dollars = Math.round(ttcpCents / 100);
    setPrices(p => ({ ...p, [id]: String(dollars) }));
    setTappedChips(s => new Set(s).add(id));
    haptic('light');
  }, []);

  const focusNext = useCallback((id: string) => {
    const idx = artworks.findIndex(a => a.id === id);
    const next = artworks[idx + 1];
    if (next && inputRefs.current[next.id]) {
      inputRefs.current[next.id]?.focus();
    }
  }, [artworks]);

  const handleSubmit = useCallback(() => {
    if (!allFilled) return;
    haptic('medium');
    const map: Record<string, number> = {};
    artworks.forEach(a => {
      map[a.id] = Math.round(parseFloat(prices[a.id]) * 100);
    });
    onSubmit(map);
  }, [allFilled, artworks, prices, onSubmit]);

  return (
    <>
      <style>{`
        @keyframes spBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes spChipPulse {
          0% { transform: scale(1); }
          50% { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
      `}</style>

      <div style={{
        position: 'fixed', inset: 0, zIndex: 210,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(10,10,10,0.45)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            animation: 'spBackdropIn 200ms ease',
          }}
        />

        {/* Sheet */}
        <div style={{
          position: 'relative', zIndex: 1,
          background: T.white,
          borderRadius: '24px 24px 0 0',
          maxHeight: '82vh',
          display: 'flex', flexDirection: 'column',
          animation: 'spSlideUp 340ms cubic-bezier(0.25, 1, 0.5, 1)',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.08), 0 -2px 12px rgba(0,0,0,0.04)',
        }}>
          {/* Drag handle */}
          <div style={{ textAlign: 'center', padding: '14px 0 6px' }}>
            <div style={{
              width: 36, height: 4, borderRadius: 2,
              background: '#E0DDD7', margin: '0 auto',
            }} />
          </div>

          {/* Header */}
          <div style={{ padding: '8px 24px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              {/* Dollar icon badge */}
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(61,171,165,0.10), rgba(61,171,165,0.04))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="16" height="16" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </div>
              <div>
                <h3 style={{
                  fontSize: 18, fontWeight: 700, color: T.text, margin: 0,
                  fontFamily: T.fontFamily,
                  letterSpacing: '-0.01em',
                }}>
                  {isSingle ? 'Log Sale' : 'Log Sales'}
                </h3>
                <p style={{
                  fontSize: 13, color: T.textMuted, margin: '2px 0 0',
                  fontFamily: T.fontFamily,
                }}>
                  {isSingle
                    ? 'Tap the asking price to use it.'
                    : `${artworks.length} pieces \u00B7 tap asking price to use it`}
                </p>
              </div>
            </div>
          </div>

          {/* Scrollable item list */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '0 20px',
            overscrollBehaviorY: 'contain',
          }}>
            {artworks.map((a, i) => {
              const isFilled = tappedChips.has(a.id) && prices[a.id] === String(Math.round(a.ttcp / 100));
              const hasValue = !!prices[a.id];
              const title = a.title || `${a.width}\u00D7${a.height}"`;

              return (
                <div key={a.id} style={{
                  padding: '16px 0',
                  borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                }}>
                  {/* Title + TTCP chip */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 10, marginBottom: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 600, color: T.text,
                        fontFamily: T.fontFamily,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        letterSpacing: '-0.01em',
                      }}>
                        {title}
                      </div>
                      <div style={{
                        fontSize: 12, color: T.textMuted, marginTop: 3,
                        fontFamily: T.fontFamily,
                      }}>
                        {a.width}&times;{a.height}&quot; &middot; {a.medium}
                      </div>
                    </div>

                    {/* TTCP chip — tappable asking price */}
                    <button
                      onClick={() => fillTTCP(a.id, a.ttcp)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '7px 14px', borderRadius: 20, flexShrink: 0,
                        background: isFilled
                          ? 'linear-gradient(135deg, rgba(61,171,165,0.14), rgba(61,171,165,0.06))'
                          : 'rgba(250,248,243,0.8)',
                        border: `1.5px solid ${isFilled ? 'rgba(61,171,165,0.3)' : T.border}`,
                        cursor: 'pointer',
                        transition: 'all 180ms ease',
                        animation: isFilled ? 'none' : undefined,
                      }}
                    >
                      <span style={{
                        fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em',
                        color: isFilled ? T.primary : T.text,
                        fontFamily: "'Manrope', 'Satoshi', 'DM Sans', system-ui, sans-serif",
                        transition: 'color 180ms',
                      }}>
                        {displayPrice(a.ttcp)}
                      </span>
                      {!isFilled && (
                        <svg width="11" height="11" fill="none" stroke={T.textMuted} strokeWidth="2.2" strokeLinecap="round" viewBox="0 0 24 24">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      )}
                      {isFilled && (
                        <svg width="11" height="11" fill="none" stroke={T.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Price input */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: hasValue ? 'rgba(250,248,243,0.6)' : T.cream,
                    borderRadius: T.radiusMd,
                    border: `1.5px solid ${hasValue ? 'rgba(61,171,165,0.35)' : '#ECEAE4'}`,
                    padding: '11px 14px',
                    transition: 'border-color 200ms, background 200ms',
                  }}>
                    <span style={{
                      color: hasValue ? T.primary : '#C4C0B8',
                      fontSize: 17, fontWeight: 700,
                      fontFamily: "'Manrope', 'Satoshi', 'DM Sans', system-ui, sans-serif",
                      transition: 'color 200ms',
                    }}>
                      $
                    </span>
                    <input
                      ref={el => { inputRefs.current[a.id] = el; }}
                      type="text"
                      inputMode="decimal"
                      value={prices[a.id] || ''}
                      onChange={e => {
                        setPrices(p => ({ ...p, [a.id]: e.target.value }));
                        if (e.target.value !== String(Math.round(a.ttcp / 100))) {
                          setTappedChips(s => { const n = new Set(s); n.delete(a.id); return n; });
                        }
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') focusNext(a.id); }}
                      placeholder="Sale price"
                      style={{
                        flex: 1, border: 'none', outline: 'none',
                        background: 'transparent',
                        fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em',
                        fontFamily: "'Manrope', 'Satoshi', 'DM Sans', system-ui, sans-serif",
                        color: T.text, caretColor: T.primary,
                      }}
                    />
                    {hasValue && (
                      <button
                        onClick={() => {
                          setPrices(p => ({ ...p, [a.id]: '' }));
                          setTappedChips(s => { const n = new Set(s); n.delete(a.id); return n; });
                          inputRefs.current[a.id]?.focus();
                        }}
                        style={{
                          background: 'rgba(0,0,0,0.04)', border: 'none',
                          borderRadius: 10, width: 22, height: 22,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', flexShrink: 0, padding: 0,
                          transition: 'background 150ms',
                        }}
                      >
                        <svg width="9" height="9" fill="none" stroke="#8A8780" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit area */}
          <div style={{
            padding: '16px 20px',
            paddingBottom: 'max(28px, env(safe-area-inset-bottom))',
          }}>
            <button
              onClick={handleSubmit}
              disabled={!allFilled}
              style={{
                width: '100%', padding: 16, borderRadius: T.radiusLg,
                background: allFilled
                  ? `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`
                  : '#ECEAE4',
                color: allFilled ? '#fff' : '#B5B2AC',
                border: 'none',
                fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em',
                cursor: allFilled ? 'pointer' : 'default',
                fontFamily: T.fontFamily,
                transition: 'all 250ms cubic-bezier(0.25, 1, 0.5, 1)',
                boxShadow: allFilled
                  ? '0 4px 16px rgba(61,171,165,0.25), 0 1px 4px rgba(61,171,165,0.15)'
                  : 'none',
              }}
            >
              {isSingle ? 'Log Sale' : `Log ${artworks.length} Sales`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
